import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, X, FileText, CheckCircle2, Search, Copy, Shield, ChevronRight, Phone, Mail, MapPin, Car, ClipboardList, Camera, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const VEHICLE_DATA = {
  "Cadillac": ["Escalade 2008-2013"],
  "Chevrolet": ["Silverado 2009-2013", "Tahoe 2007-2013"],
  "Chrysler": ["Aspen 2009"],
  "Dodge": ["Durango"],
  "Ford": ["Escape 2005-2013", "C-Max 2013-2018", "C-Max Energi", "Fusion 2010-2012", "Fusion 2013-2018", "Fusion Energi"],
  "GMC": ["Sierra 2007-2013", "Yukon 2007-2013"],
  "Honda": ["Accord 2005-2007", "Civic 2003-2005", "Civic 2006-2011", "CRZ 2010-2012", "Insight 2000-2006", "Insight 2010-2014"],
  "Hyundai": ["Sonata 2011-2015"],
  "Kia": ["Optima 2010-2014"],
  "Lexus": ["CT 200h", "ES 300h", "GS 450h", "HS 250H", "NX 300H 2015-2019", "RX 400h", "RX 450h"],
  "Lincoln": ["MKZ 2010-2012", "MKZ 2013-2018"],
  "Mazda": ["Tribute 2004-2013"],
  "Mercury": ["Mariner 2005-2010", "Milan 2010-2013"],
  "Nissan": ["Altima 2007-2011"],
  "Toyota": ["Avalon 2012-2016", "Camry 2007-2011", "Camry 2012-2017", "Highlander 2006-2009", "Highlander 2010-2015", "Highlander 2016-2019", "Prius 2001-2003", "Prius 2004-2009", "Prius 2010-2015", "Prius 2016-2020", "Prius C", "Prius V 2012-2015", "Rav4 2016-2018"]
};

const SectionTitle = ({ icon: Icon, title, number }) => (
  <div className="flex items-center gap-3 mb-6 pt-2">
    <div className="w-8 h-8 rounded-full bg-[#0055FF] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
      {number}
    </div>
    <div className="flex items-center gap-2">
      <Icon className="w-5 h-5 text-[#0055FF]" />
      <h3 className="text-lg font-semibold text-[#111]">{title}</h3>
    </div>
    <div className="flex-1 h-px bg-gray-200 ml-2" />
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_email_confirm: "",
    customer_phone: "",
    customer_phone_confirm: "",
    vehicle_make: "",
    vehicle_model: "",
    street_address: "",
    apartment_unit: "",
    city: "",
    state: "",
    zip_code: "",
    additional_notes: "",
    invoice_number: "",
    invoice_number_confirm: "",
  });
  const [file, setFile] = useState(null);
  const [diagnosticCodeImages, setDiagnosticCodeImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const resetForm = () => {
    setFormData({ customer_name: "", customer_email: "", customer_email_confirm: "", customer_phone: "", customer_phone_confirm: "", vehicle_make: "", vehicle_model: "", street_address: "", apartment_unit: "", city: "", state: "", zip_code: "", additional_notes: "", invoice_number: "", invoice_number_confirm: "" });
    setFile(null); setDiagnosticCodeImages([]); setSubmitted(false); setClaimNumber(""); setValidationErrors({});
  };
  const [claimNumber, setClaimNumber] = useState("");
  const [checkClaimNumber, setCheckClaimNumber] = useState("");
  const [claimStatus, setClaimStatus] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [mapsLoaded, setMapsLoaded] = useState(!!window.google?.maps?.places);

  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return;
    if (window.google?.maps?.places) { setMapsLoaded(true); return; }
    if (document.getElementById("google-maps-script")) return;
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => setMapsLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!mapsLoaded || !addressInputRef.current || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
    });
    autocompleteRef.current = ac;
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place.address_components) return;
      let street_number = "", route = "", city = "", state = "", zip = "";
      for (const comp of place.address_components) {
        const type = comp.types[0];
        if (type === "street_number") street_number = comp.long_name;
        else if (type === "route") route = comp.long_name;
        else if (type === "locality" || type === "sublocality_level_1") city = comp.long_name;
        else if (type === "administrative_area_level_1") state = comp.short_name;
        else if (type === "postal_code") zip = comp.long_name;
      }
      setFormData(prev => ({
        ...prev,
        street_address: `${street_number} ${route}`.trim(),
        city: city,
        state: state || prev.state,
        zip_code: zip,
      }));
    });
  }, [mapsLoaded]);
  const [customVehicle, setCustomVehicle] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: value,
      ...(name === 'vehicle_make' ? { vehicle_model: '' } : {})
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setFile(selectedFile);
    }
  };

  const MAX_PHOTO_BYTES = 25 * 1024 * 1024;
  const isImageFile = (f) => f.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|jfif|avif)$/i.test(f.name || "");

  const handleDiagnosticCodeImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    e.target.value = "";
    if (diagnosticCodeImages.length + selectedFiles.length > 5) {
      toast.error("You can upload a maximum of 5 diagnostic code images");
      return;
    }
    for (const selectedFile of selectedFiles) {
      if (!isImageFile(selectedFile)) {
        toast.error(`${selectedFile.name} is not an image file`);
        continue;
      }
      if (selectedFile.size > MAX_PHOTO_BYTES) {
        toast.error(`${selectedFile.name} is too large (max 25MB)`);
        continue;
      }
    }
    const validFiles = selectedFiles.filter(f => isImageFile(f) && f.size <= MAX_PHOTO_BYTES);
    setDiagnosticCodeImages(prev => [...prev, ...validFiles]);
  };

  const removeFile = () => setFile(null);
  const removeDiagnosticCodeImage = (index) => {
    setDiagnosticCodeImages(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const errors = {};
    const digits = (v) => (v || "").replace(/\D/g, "");
    if (formData.customer_email.trim().toLowerCase() !== formData.customer_email_confirm.trim().toLowerCase()) errors.email = "Email addresses do not match";
    if (digits(formData.customer_phone) !== digits(formData.customer_phone_confirm)) errors.phone = "Phone numbers do not match";
    if ((formData.invoice_number || formData.invoice_number_confirm) && formData.invoice_number.trim() !== formData.invoice_number_confirm.trim()) errors.invoice = "Invoice numbers do not match";
    if (diagnosticCodeImages.length === 0) errors.diagnosticImage = "Please upload at least one diagnostic code photo";
    setValidationErrors(errors);
    const first = Object.keys(errors)[0];
    if (first) {
      const anchor = { email: "customer_email", phone: "customer_phone", invoice: "invoice_number", diagnosticImage: "diagnostic-code-image-section" }[first];
      setTimeout(() => document.querySelector(`[name="${anchor}"], #${anchor}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    }
    return Object.keys(errors).length === 0;
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    if (!checkClaimNumber.trim()) { toast.error("Please enter a claim number"); return; }
    setIsCheckingStatus(true);
    setClaimStatus(null);
    try {
      const response = await axios.get(`${API}/claims/${checkClaimNumber.trim()}`);
      setClaimStatus(response.data);
    } catch (error) {
      if (error.response?.status === 404) toast.error("Claim number not found");
      else toast.error("Failed to check claim status");
      setClaimStatus(null);
    } finally { setIsCheckingStatus(false); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending Review": return "bg-amber-50 text-amber-700 border-amber-200";
      case "Missing Information": return "bg-orange-50 text-orange-700 border-orange-200";
      case "Info Verified": return "bg-blue-50 text-blue-700 border-blue-200";
      case "Scheduled": return "bg-violet-50 text-violet-700 border-violet-200";
      case "Completed": return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Denied": return "bg-red-50 text-red-700 border-red-200";
      default: return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { toast.error("Please fix the errors before submitting"); return; }
    setIsSubmitting(true);
    try {
      let invoice_file_path = null;
      let diagnostic_code_image_paths = [];
      const totalFiles = diagnosticCodeImages.length + (file ? 1 : 0);
      let done = 0;
      const uploadOne = async (f, label) => {
        setUploadProgress({ current: done + 1, total: totalFiles, percent: 0, label });
        const fd = new FormData();
        fd.append("file", f, f.name || "photo.jpg");
        try {
          const res = await axios.post(`${API}/claims/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
            onUploadProgress: (ev) => ev.total && setUploadProgress((p) => ({ ...p, percent: Math.round((ev.loaded / ev.total) * 100) })),
          });
          done += 1;
          return res.data.file_path;
        } catch (err) {
          throw new Error(`Could not upload "${f.name}": ${err.response?.data?.detail || "network error, please try again"}`);
        }
      };
      if (totalFiles > 0) setIsUploading(true);
      for (let i = 0; i < diagnosticCodeImages.length; i++) {
        diagnostic_code_image_paths.push(await uploadOne(diagnosticCodeImages[i], `photo ${i + 1} of ${diagnosticCodeImages.length}`));
      }
      if (file) invoice_file_path = await uploadOne(file, "invoice");
      setIsUploading(false);
      const claimData = {
        customer_name: formData.customer_name, customer_email: formData.customer_email, customer_phone: formData.customer_phone,
        vehicle_year: formData.vehicle_year || "", vehicle_make: formData.vehicle_make, vehicle_model: formData.vehicle_model,
        street_address: formData.street_address, apartment_unit: formData.apartment_unit, city: formData.city, state: formData.state, zip_code: formData.zip_code,
        additional_notes: formData.additional_notes, diagnostic_code_image_paths, invoice_file_path,
      };
      if (formData.invoice_number) claimData.invoice_number = formData.invoice_number;
      const response = await axios.post(`${API}/claims`, claimData);
      toast.success("Claim submitted successfully!");
      setClaimNumber(response.data.claim_number);
      setSubmitted(true);
      window.scrollTo({ top: document.getElementById("claim-form-section")?.offsetTop ?? 0, behavior: "smooth" });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to submit claim. Please try again.");
    } finally { setIsSubmitting(false); setIsUploading(false); }
  };

  const inputClass = "h-12 text-base bg-gray-50 border-transparent rounded-lg focus:bg-white focus:border-transparent focus:ring-2 focus:ring-[#0055FF] text-[#111] font-medium transition-all duration-200";
  const labelClass = "text-sm font-semibold tracking-wide text-gray-700 mb-1.5 block";

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* Left Column - Branding (sticky on desktop) */}
      <div className="lg:w-[38%] lg:min-h-screen lg:sticky lg:top-0 bg-[#F7F7F9] flex flex-col justify-between p-8 lg:p-12">
        {/* Top - Brand */}
        <div>
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1 className="text-2xl font-bold text-[#111] tracking-tight" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}>
                Hybrid<span className="text-[#0055FF]">2Go</span>
              </h1>
              <p className="text-xs text-gray-500 tracking-wide mt-0.5">WARRANTY SERVICE</p>
            </div>
            <button
              onClick={() => navigate("/h2g")}
              className="opacity-0 w-8 h-8 cursor-default"
              data-testid="admin-button"
              aria-label="Admin"
            />
          </div>

          {/* Hero Text */}
          <div className="mb-8">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#111] tracking-tight leading-tight mb-4" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}>
              Hybrid Battery<br />
              <span className="text-[#0055FF]">Warranty Claims</span>
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Submit your claim quickly and easily. We'll review your information and get back to you within 24-48 hours.
            </p>
          </div>

          {/* Trust Points */}
          <div className="space-y-3 mb-8">
            {["Fast processing within 24-48 hours", "Expert hybrid battery service", "Comprehensive warranty coverage"].map((text, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <p className="text-base text-gray-700">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Check Claim Status Widget */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_10px_40px_rgba(0,0,0,0.06)] border border-gray-100">
          <h3 className="text-base font-semibold text-[#111] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}>
            <Search className="w-4 h-4 text-[#0055FF]" />
            Check Claim Status
          </h3>
          <form onSubmit={handleCheckStatus} className="space-y-3">
            <Input
              type="text"
              data-testid="check-claim-input"
              placeholder="Enter claim number"
              value={checkClaimNumber}
              onChange={(e) => setCheckClaimNumber(e.target.value)}
              className="h-11 text-sm bg-gray-50 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#0055FF]"
            />
            <Button
              type="submit"
              data-testid="check-status-button"
              disabled={isCheckingStatus}
              className="w-full h-11 bg-[#111] hover:bg-[#222] text-white rounded-full text-sm font-semibold transition-all hover:scale-[1.02] active:scale-100"
            >
              {isCheckingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              {isCheckingStatus ? "Checking..." : "Check Status"}
            </Button>
          </form>

          {claimStatus && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl" data-testid="claim-status-result">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-[#111] text-sm">{claimStatus.customer_name}</span>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(claimStatus.status)}`}>
                  {claimStatus.status}
                </span>
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <p>Claim #{claimStatus.claim_number}</p>
                <p>Submitted {new Date(claimStatus.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Form */}
      <div className="lg:w-[62%] min-h-screen">
        <div id="claim-form-section" className="max-w-2xl mx-auto px-5 sm:px-6 py-10 lg:px-16 lg:py-16">
          {submitted ? (
            /* Success State */
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="success-message">
              <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
              <h2 className="text-3xl font-semibold text-[#111] mb-3 tracking-tight" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}>
                Claim Submitted
              </h2>
              <p className="text-gray-500 text-base mb-8 max-w-sm">
                We've received your warranty claim and will review your information shortly.
              </p>
              <div className="bg-[#F7F7F9] rounded-2xl p-8 w-full max-w-sm border border-gray-100">
                <p className="text-xs font-semibold tracking-[0.08em] uppercase text-gray-500 mb-3">Your Claim Number</p>
                <div className="flex items-center justify-center gap-3">
                  <code className="text-3xl font-bold text-[#111] tracking-widest" data-testid="claim-number-display">{claimNumber}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(claimNumber); toast.success("Copied!"); }}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid="copy-claim-number"
                  >
                    <Copy className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-3">Save this number to track your claim</p>
              </div>
              <p className="text-sm text-gray-500 mt-6 max-w-sm">We've also texted and emailed this number to you.</p>
              <Button type="button" variant="outline" onClick={resetForm} className="mt-6 h-12 rounded-full px-6" data-testid="submit-another-claim-button">
                Submit another claim
              </Button>
            </div>
          ) : (
            <>
              {/* Form Header */}
              <div className="mb-12">
                <h2 className="text-3xl font-semibold text-[#111] tracking-tight mb-2" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}>
                  Start Your Claim
                </h2>
                <p className="text-gray-600 text-base">Fill out the details below and we'll begin processing your warranty claim.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-10">
                {/* Section 1: Personal Info */}
                <div>
                  <SectionTitle icon={Mail} title="Personal Information" number="1" />
                  <div className="space-y-5">
                    <div>
                      <label className={labelClass}>Full Name</label>
                      <Input
                        name="customer_name" data-testid="customer-name-input" type="text" required autoComplete="name"
                        value={formData.customer_name} onChange={handleInputChange}
                        className={inputClass} placeholder="John Doe"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Email Address</label>
                        <Input
                          name="customer_email" data-testid="customer-email-input" type="email" required autoComplete="email" inputMode="email"
                          value={formData.customer_email} onChange={handleInputChange}
                          className={inputClass} placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Confirm Email</label>
                        <Input
                          name="customer_email_confirm" data-testid="customer-email-confirm-input" type="email" required autoComplete="off" inputMode="email"
                          value={formData.customer_email_confirm} onChange={handleInputChange}
                          className={`${inputClass} ${validationErrors.email ? 'ring-2 ring-red-400' : ''}`}
                          placeholder="john@example.com"
                        />
                        {validationErrors.email && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.email}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Phone Number</label>
                        <Input
                          name="customer_phone" data-testid="customer-phone-input" type="tel" required autoComplete="tel"
                          value={formData.customer_phone} onChange={handleInputChange}
                          className={inputClass} placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Confirm Phone</label>
                        <Input
                          name="customer_phone_confirm" data-testid="customer-phone-confirm-input" type="tel" required autoComplete="off"
                          value={formData.customer_phone_confirm} onChange={handleInputChange}
                          className={`${inputClass} ${validationErrors.phone ? 'ring-2 ring-red-400' : ''}`}
                          placeholder="(555) 123-4567"
                        />
                        {validationErrors.phone && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.phone}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Vehicle Details */}
                <div>
                  <SectionTitle icon={Car} title="Vehicle Details" number="2" />
                  {customVehicle ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className={labelClass}>Vehicle Year</label>
                        <Input
                          name="vehicle_year" type="text" inputMode="numeric" maxLength={4}
                          value={formData.vehicle_year || ""} onChange={handleInputChange}
                          className={inputClass} placeholder="e.g. 2012"
                          data-testid="vehicle-year-custom"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Vehicle Make</label>
                        <Input
                          name="vehicle_make" type="text" required
                          value={formData.vehicle_make} onChange={handleInputChange}
                          className={inputClass} placeholder="e.g. Toyota"
                          data-testid="vehicle-make-custom"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Vehicle Model</label>
                        <Input
                          name="vehicle_model" type="text" required
                          value={formData.vehicle_model} onChange={handleInputChange}
                          className={inputClass} placeholder="e.g. Prius"
                          data-testid="vehicle-model-custom"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className={labelClass}>Vehicle Year</label>
                        <Input
                          name="vehicle_year" type="text" inputMode="numeric" maxLength={4}
                          value={formData.vehicle_year || ""} onChange={handleInputChange}
                          className={inputClass} placeholder="e.g. 2012"
                          data-testid="vehicle-year-input"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Vehicle Make</label>
                        <Select value={formData.vehicle_make} onValueChange={(value) => setFormData(prev => ({ ...prev, vehicle_make: value, vehicle_model: '' }))}>
                          <SelectTrigger className={inputClass} data-testid="vehicle-make-select">
                            <SelectValue placeholder="Select make" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(VEHICLE_DATA).map((make) => (
                              <SelectItem key={make} value={make}>{make}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className={labelClass}>Vehicle Model</label>
                        <Select value={formData.vehicle_model} onValueChange={(value) => setFormData(prev => ({ ...prev, vehicle_model: value }))} disabled={!formData.vehicle_make}>
                          <SelectTrigger className={inputClass} data-testid="vehicle-model-select">
                            <SelectValue placeholder={formData.vehicle_make ? "Select model" : "Select make first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.vehicle_make && VEHICLE_DATA[formData.vehicle_make]?.map((model) => (
                              <SelectItem key={model} value={model}>{model}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => { setCustomVehicle(!customVehicle); setFormData(prev => ({ ...prev, vehicle_make: '', vehicle_model: '', vehicle_year: '' })); }}
                    className="text-sm text-[#0055FF] hover:text-[#0044CC] font-medium mt-2 py-3 -my-1 block min-h-[44px]"
                    data-testid="toggle-custom-vehicle"
                  >
                    {customVehicle ? "Back to vehicle list" : "Don't see your vehicle? Enter manually"}
                  </button>
                </div>

                {/* Section 3: Service Address */}
                <div>
                  <SectionTitle icon={MapPin} title="Service Address" number="3" />
                  <p className="text-sm text-gray-500 mb-4 -mt-3">Where our technician will come for warranty service</p>
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Street Address</label>
                      <Input
                        name="street_address" data-testid="street-address-input" required autoComplete="address-line1"
                        ref={addressInputRef}
                        value={formData.street_address} onChange={handleInputChange}
                        className={inputClass} placeholder="Start typing an address..."
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Apartment / Unit <span className="text-gray-400 normal-case tracking-normal">(optional)</span></label>
                      <Input
                        name="apartment_unit" data-testid="apartment-unit-input" autoComplete="address-line2"
                        value={formData.apartment_unit} onChange={handleInputChange}
                        className={inputClass} placeholder="Apt 4B, Suite 200, etc."
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <label className={labelClass}>City</label>
                        <Input
                          name="city" data-testid="city-input" required autoComplete="address-level2"
                          value={formData.city} onChange={handleInputChange}
                          className={inputClass} placeholder="Los Angeles"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>State</label>
                        <Input
                          name="state" data-testid="state-input" required maxLength="2" autoComplete="address-level1" autoCapitalize="characters"
                          value={formData.state} onChange={handleInputChange}
                          className={inputClass} placeholder="CA"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>ZIP</label>
                        <Input
                          name="zip_code" data-testid="zip-code-input" required pattern="[0-9]{5}(-[0-9]{4})?" autoComplete="postal-code" inputMode="numeric"
                          value={formData.zip_code} onChange={handleInputChange}
                          className={inputClass} placeholder="90210"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Additional Notes */}
                <div>
                  <label className={labelClass}>Additional Notes <span className="text-gray-400 normal-case tracking-normal">(optional)</span></label>
                  <Textarea
                    name="additional_notes" data-testid="additional-notes-input"
                    value={formData.additional_notes} onChange={handleInputChange}
                    className="min-h-24 text-base bg-gray-50 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#0055FF] text-[#111] transition-all duration-200"
                    placeholder="Battery issues, preferred appointment times, access instructions..."
                  />
                </div>

                {/* Section 5: Diagnostics & Documents */}
                <div>
                  <SectionTitle icon={ClipboardList} title="Diagnostics & Documents" number="4" />

                  {/* Diagnostic Code Images */}
                  <div className="mb-6">
                    <label className={labelClass}>
                      Diagnostic Code Images <span className="text-red-500 normal-case tracking-normal">* Required (up to 5)</span>
                    </label>
                    <div id="diagnostic-code-image-section">
                    {diagnosticCodeImages.length < 5 && (
                      <div
                        className={`border-2 border-dashed rounded-xl p-5 sm:p-8 text-center transition-all duration-200 ${validationErrors.diagnosticImage ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-gray-50/50'}`}
                        data-testid="diagnostic-code-image-upload-area"
                      >
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-base font-medium text-gray-600">Add photos of the dashboard warning or code reader</p>
                        <p className="text-sm text-gray-400 mt-1">iPhone & Android photos welcome (HEIC/JPG/PNG) · {diagnosticCodeImages.length}/5 added</p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center mt-4">
                          <label htmlFor="diagnostic-code-image-camera" className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full bg-[#0055FF] text-white font-semibold cursor-pointer active:scale-[0.98] transition-transform sm:hidden" data-testid="take-photo-button">
                            <Camera className="w-5 h-5" /> Take a photo
                          </label>
                          <label htmlFor="diagnostic-code-image-upload" className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full bg-white border border-gray-300 text-gray-800 font-semibold cursor-pointer active:scale-[0.98] transition-transform hover:bg-gray-50" data-testid="choose-photos-button">
                            <ImageIcon className="w-5 h-5" /> Choose from library
                          </label>
                        </div>
                        <input id="diagnostic-code-image-camera" type="file" className="hidden" accept="image/*" capture="environment" onChange={handleDiagnosticCodeImageChange} />
                        <input id="diagnostic-code-image-upload" type="file" multiple className="hidden" accept="image/*,.heic,.heif" onChange={handleDiagnosticCodeImageChange} />
                      </div>
                    )}
                    {diagnosticCodeImages.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {diagnosticCodeImages.map((image, index) => (
                          <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200" data-testid={`diagnostic-code-image-${index}`}>
                            <PhotoThumb file={image} />
                            <button type="button" onClick={() => removeDiagnosticCodeImage(index)} className="absolute top-1 right-1 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center active:bg-black/80" data-testid={`remove-diagnostic-code-image-${index}`} aria-label="Remove photo">
                              <X className="w-4 h-4" />
                            </button>
                            <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1 py-0.5 truncate">{(image.size / 1024 / 1024).toFixed(1)} MB</span>
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                    {validationErrors.diagnosticImage && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.diagnosticImage}</p>}
                  </div>

                  {/* Invoice Number */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className={labelClass}>Invoice Number <span className="text-gray-400 normal-case tracking-normal">(optional - speeds up processing)</span></label>
                      <Input
                        name="invoice_number" data-testid="invoice-number-input" type="text"
                        value={formData.invoice_number} onChange={handleInputChange}
                        className={inputClass} placeholder="INV-12345"
                      />
                    </div>
                    {(formData.invoice_number || formData.invoice_number_confirm) && (
                      <div>
                        <label className={labelClass}>Confirm Invoice Number</label>
                        <Input
                          name="invoice_number_confirm" data-testid="invoice-number-confirm-input" type="text"
                          value={formData.invoice_number_confirm} onChange={handleInputChange}
                          className={`${inputClass} ${validationErrors.invoice ? 'ring-2 ring-red-400' : ''}`}
                          placeholder="INV-12345"
                        />
                        {validationErrors.invoice && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.invoice}</p>}
                      </div>
                    )}
                  </div>

                  {/* Invoice Document Upload */}
                  <div>
                    <label className={labelClass}>Invoice Document <span className="text-gray-400 normal-case tracking-normal">(optional)</span></label>
                    {!file ? (
                      <label
                        htmlFor="file-upload"
                        className="block border-2 border-dashed border-gray-200 bg-gray-50/50 rounded-xl p-6 text-center cursor-pointer transition-all duration-200 hover:bg-gray-50"
                        data-testid="file-upload-area"
                      >
                        <Upload className="w-7 h-7 text-gray-400 mx-auto mb-2" />
                        <p className="text-base font-medium text-gray-600">Click to upload invoice</p>
                        <p className="text-sm text-gray-400 mt-1">PDF, JPG, PNG up to 10MB</p>
                        <input id="file-upload" type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />
                      </label>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="file-selected">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <span className="text-sm font-medium text-gray-800">{file.name}</span>
                          <span className="text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB</span>
                        </div>
                        <button type="button" onClick={removeFile} className="text-gray-400 hover:text-red-500 transition-colors" data-testid="remove-file-button">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <Button
                    type="submit" data-testid="submit-claim-button" disabled={isSubmitting}
                    className="w-full h-14 text-base font-semibold bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-full shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-100 transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{isUploading && uploadProgress ? `Uploading ${uploadProgress.label}… ${uploadProgress.percent}%` : "Submitting..."}</>
                    ) : (
                      <>Submit Warranty Claim <ChevronRight className="w-5 h-5 ml-1" /></>
                    )}
                  </Button>
                  <p className="text-center text-xs text-gray-400 mt-4">
                    Your information is secure and will only be used for warranty processing.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;

const PhotoThumb = ({ file }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (/\.(heic|heif)$/i.test(file.name) || file.type === "image/heic" || file.type === "image/heif") return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  if (!url) return <div className="w-full h-full flex flex-col items-center justify-center text-gray-400"><FileText className="w-6 h-6" /><span className="text-[10px] mt-1">Photo</span></div>;
  return <img src={url} alt="" className="w-full h-full object-cover" />;
};
